"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import sharedStyles from "./FeaturesSection.module.css";
import demoStyles from "./EmbedFeaturesSection.module.css";
import LazyOnVisible from "./LazyOnVisible";
import {
  EmbedCodeDemo,
  AnalyticsGraphDemo,
  LibraryDemo,
} from "./FeaturesSection";

const DEMO_LOADERS = {
  themes: () => import("./ThemesSplitDemo"),
  conversion: () => import("./OptimizationDemo"),
  "low-latency": () => import("./LowLatencyCobeGlobe"),
};

const features = [
  {
    id: "themes",
    title: "Customizable player themes",
    description: "",
  },
  {
    id: "conversion",
    title: "Increase click through rate",
    description: "Our player has a higher CTR",
  },
  {
    id: "embed",
    title: "Embed your videos",
    description: "",
  },
  {
    id: "analytics",
    title: "Analytics & Data",
    description: "Views, engagement, and more",
  },
  {
    id: "low-latency",
    title: "Low latency streaming",
    description:
      "Fast and consistent video delivery",
  },
  {
    id: "library",
    title: "Cloud Library & Organization",
    description: "",
  },
];

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export default function EmbedFeaturesSection() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add(sharedStyles.visible);
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    cardsRef.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback((e, index) => {
    const card = cardsRef.current[index];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    card.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    card.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseLeave = useCallback((index) => {
    const card = cardsRef.current[index];
    if (!card) return;
    card.style.setProperty("--mouse-x", `-1000px`);
    card.style.setProperty("--mouse-y", `-1000px`);
  }, []);

  const renderDemo = (id) => {
    if (DEMO_LOADERS[id]) {
      return <LazyOnVisible loader={DEMO_LOADERS[id]} />;
    }
    if (id === "embed") return <EmbedCodeDemo />;
    if (id === "analytics") return <AnalyticsGraphDemo />;
    if (id === "library") return <LibraryDemo />;
    return null;
  };

  return (
    <section className={sharedStyles.features}>
      <div className={sharedStyles.container}>
        <div className={sharedStyles.header} ref={sectionRef}>
          <h2 className={sharedStyles.title}>
            Improve the videos that drive your business
          </h2>
          <p className={sharedStyles.subtitle}>
            Fully custom player, advanced analytics and smooth low latency delivery 
          </p>
        </div>

        <div className={sharedStyles.bentoGrid}>
          {features.map((feature, index) => {
            const isLowLatency = feature.id === "low-latency";
            return (
              <div
                key={feature.id}
                className={`${sharedStyles.featureCard} ${isLowLatency ? demoStyles.cardLowLatency : ""}`}
                ref={(el) => (cardsRef.current[index] = el)}
                style={{ transitionDelay: `${index * 80}ms` }}
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => handleMouseLeave(index)}
              >
                <div className={sharedStyles.cardContent}>
                  <h3 className={sharedStyles.featureTitle}>{feature.title}</h3>
                  <p className={sharedStyles.featureDescription}>{feature.description}</p>
                </div>

                <div className={sharedStyles.imageContainer}>
                  {renderDemo(feature.id)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
